"use client";

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import { OneTimePackage, SubscriptionPlan, AddOn } from "@/types";
import { packageService } from "@/services/packageService";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

interface PackageContextType {
  oneTimePackages: OneTimePackage[];
  subscriptionPlans: SubscriptionPlan[];
  addOns: AddOn[];
  loading: boolean;
  error: string | null;
  
  // OneTimePackage operations
  addOneTimePackage: (pkg: Omit<OneTimePackage, "id">) => Promise<OneTimePackage>;
  updateOneTimePackage: (id: string, pkg: Partial<OneTimePackage>) => void;
  deleteOneTimePackage: (id: string) => Promise<void>;
  
  // SubscriptionPlan operations
  addSubscriptionPlan: (plan: Omit<SubscriptionPlan, "id">) => SubscriptionPlan;
  updateSubscriptionPlan: (id: string, plan: Partial<SubscriptionPlan>) => void;
  
  // AddOn operations
  addAddOn: (addOn: Omit<AddOn, "id">) => AddOn;
  updateAddOn: (id: string, addOn: Partial<AddOn>) => void;
  deleteAddOn: (id: string) => void;
  
  // Fetch operations
  fetchPackages: () => Promise<void>;
  refreshPackages: () => Promise<void>;
}

const PackageContext = createContext<PackageContextType | undefined>(undefined);

export function PackageProvider({ children }: { children: ReactNode }) {
  const [oneTimePackages, setOneTimePackages] = useState<OneTimePackage[]>([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [addOns, setAddOns] = useState<AddOn[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const fetchPackages = useCallback(async () => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [oneTime, subscription, addOn] = await Promise.all([
        packageService.getOneTimePackages(),
        packageService.getSubscriptionPlans(),
        // packageService.getAddOns(),
      ]);
      setOneTimePackages([...oneTime].sort((a, b) => a.name.localeCompare(b.name)));
      setSubscriptionPlans([...subscription].sort((a, b) => a.name.localeCompare(b.name)));
      // setAddOns(addOn);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch packages";
      setError(errorMessage);
      console.error("Error fetching packages:", error);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const refreshPackages = useCallback(async () => {
    await fetchPackages();
  }, [fetchPackages]);

  // OneTimePackage operations
  const addOneTimePackage = useCallback(
    async (pkgData: Omit<OneTimePackage, "id">): Promise<OneTimePackage> => {
      try {
        // Transform services array to features array for API
        // services is already an array of strings, use it directly as features
        const features = pkgData.services || [];

        // Create package via API
        const newPackage = await packageService.createPackage({
          name: pkgData.name,
          price: pkgData.price,
          features: features,
        });

        // Update local state with the created package
        setOneTimePackages((prev) => [newPackage, ...prev]);

        // Refresh packages list to ensure consistency
        await fetchPackages();
        return newPackage;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to create package";
        setError(errorMessage);
        console.error("Error creating package:", error);
        toast.error(errorMessage);
        throw error;
      }
    },
    [fetchPackages]
  );

  const updateOneTimePackage = useCallback(
    (id: string, pkgData: Partial<OneTimePackage>) => {
      setOneTimePackages((prev) =>
        prev.map((p) => (p.id === id ? { ...p, ...pkgData } : p))
      );
    },
    []
  );

  const deleteOneTimePackage = useCallback(
    async (id: string): Promise<void> => {
      try {
        // Call API to delete package
        await packageService.deletePackage(id);

        // Remove from local state after successful deletion
        setOneTimePackages((prev) => prev.filter((p) => p.id !== id));

        // Refresh packages list to ensure consistency
        await fetchPackages();

        toast.success("Package deleted successfully");
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to delete package";
        setError(errorMessage);
        console.error("Error deleting package:", error);
        toast.error(errorMessage);
        throw error;
      }
    },
    [fetchPackages]
  );

  // SubscriptionPlan operations
  const addSubscriptionPlan = useCallback(
    (planData: Omit<SubscriptionPlan, "id">) => {
      let newPlan: SubscriptionPlan;
      setSubscriptionPlans((prev) => {
        newPlan = {
          ...planData,
          id: `S${String(prev.length + 1).padStart(3, "0")}`,
        };
        return [newPlan, ...prev];
      });
      return newPlan!;
    },
    []
  );

  const updateSubscriptionPlan = useCallback(
    (id: string, planData: Partial<SubscriptionPlan>) => {
      setSubscriptionPlans((prev) =>
        prev.map((p) => (p.id === id ? { ...p, ...planData } : p))
      );
    },
    []
  );

  // AddOn operations
  const addAddOn = useCallback((addOnData: Omit<AddOn, "id">) => {
    let newAddOn: AddOn;
    setAddOns((prev) => {
      newAddOn = {
        ...addOnData,
        id: `A${String(prev.length + 1).padStart(3, "0")}`,
      };
      return [newAddOn, ...prev];
    });
    return newAddOn!;
  }, []);

  const updateAddOn = useCallback((id: string, addOnData: Partial<AddOn>) => {
    setAddOns((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...addOnData } : a))
    );
  }, []);

  const deleteAddOn = useCallback((id: string) => {
    setAddOns((prev) => prev.filter((a) => a.id !== id));
  }, []);

  // Fetch packages on mount, but only if authenticated
  useEffect(() => {
    // Wait for auth to finish loading, then check if authenticated
    if (!authLoading && isAuthenticated) {
      fetchPackages();
    }
  }, [fetchPackages, authLoading, isAuthenticated]);

  const value: PackageContextType = {
    oneTimePackages,
    subscriptionPlans,
    addOns,
    loading,
    error,
    addOneTimePackage,
    updateOneTimePackage,
    deleteOneTimePackage,
    addSubscriptionPlan,
    updateSubscriptionPlan,
    addAddOn,
    updateAddOn,
    deleteAddOn,
    fetchPackages,
    refreshPackages,
  };

  return <PackageContext.Provider value={value}>{children}</PackageContext.Provider>;
}

export function usePackages() {
  const context = useContext(PackageContext);
  if (context === undefined) {
    throw new Error("usePackages must be used within a PackageProvider");
  }
  return context;
}

